import { useRef } from 'react';
import type { VideoOverlay, VideoAnimation } from '../types';
import { FONT_OPTIONS, ANIMATION_OPTIONS } from '../types';

interface VideoEditorPropertiesProps {
  selectedOverlay: VideoOverlay | null;
  onUpdateOverlay: (updates: Partial<VideoOverlay>) => void;
  onDeleteOverlay: () => void;
  onDuplicateOverlay: () => void;
  onReplaceLogoImage: (file: File) => void;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">{children}</p>
  );
}

function ColorSwatch({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => inputRef.current?.click()}
        className="w-7 h-7 rounded-lg border-2 border-stone-700 cursor-pointer shrink-0 transition-transform hover:scale-110"
        style={{ backgroundColor: value }}
        title={label}
      />
      <input
        ref={inputRef}
        type="color"
        value={value.startsWith('rgba') ? '#ffffff' : value}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only"
      />
      <span className="text-xs text-stone-400 font-mono">{value}</span>
    </div>
  );
}

export default function VideoEditorProperties({
  selectedOverlay,
  onUpdateOverlay,
  onDeleteOverlay,
  onDuplicateOverlay,
  onReplaceLogoImage,
}: VideoEditorPropertiesProps) {
  const logoReplaceRef = useRef<HTMLInputElement>(null);

  const handleLogoReplace = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onReplaceLogoImage(file);
    e.target.value = '';
  };

  if (!selectedOverlay) {
    return (
      <div className="w-64 bg-stone-900 border-l border-stone-800 flex flex-col overflow-hidden shrink-0">
        <div className="px-4 pt-4 pb-3 border-b border-stone-800">
          <p className="text-sm font-bold text-white">Properties</p>
          <p className="text-xs text-stone-500 mt-0.5">Select an element to edit</p>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-3 px-4 text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-stone-800">
            <i className="ri-cursor-line text-stone-600 text-xl" />
          </div>
          <p className="text-stone-600 text-xs leading-relaxed">
            Click on any element in the canvas to select it and edit its properties here.
          </p>
        </div>
      </div>
    );
  }

  const isText = selectedOverlay.type === 'text';
  const isCta = selectedOverlay.type === 'cta';
  const isLogo = selectedOverlay.type === 'logo';

  return (
    <div className="w-64 bg-stone-900 border-l border-stone-800 flex flex-col overflow-hidden shrink-0">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-stone-800 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-white capitalize">{selectedOverlay.type} Properties</p>
            <p className="text-xs text-stone-500 mt-0.5 truncate max-w-[160px]">
              {isLogo ? 'Logo / Image' : selectedOverlay.content || 'No content'}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onDuplicateOverlay}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-stone-500 hover:text-stone-300 hover:bg-stone-800 transition-colors cursor-pointer"
              title="Duplicate"
            >
              <i className="ri-file-copy-line text-sm" />
            </button>
            <button
              onClick={onDeleteOverlay}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-stone-500 hover:text-red-400 hover:bg-stone-800 transition-colors cursor-pointer"
              title="Delete"
            >
              <i className="ri-delete-bin-line text-sm" />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">

        {/* Content (text/cta) */}
        {(isText || isCta) && (
          <div>
            <SectionLabel>Content</SectionLabel>
            <textarea
              value={selectedOverlay.content}
              onChange={(e) => onUpdateOverlay({ content: e.target.value })}
              rows={isText ? 3 : 1}
              className="w-full resize-none rounded-lg border border-stone-700 bg-stone-800 px-3 py-2 text-sm text-stone-200 placeholder-stone-600 focus:outline-none focus:border-orange-500 transition-colors"
              placeholder={isCta ? 'Button text...' : 'Enter text...'}
            />
          </div>
        )}

        {/* Logo replace */}
        {isLogo && (
          <div>
            <SectionLabel>Logo Image</SectionLabel>
            <button
              onClick={() => logoReplaceRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-stone-700 hover:border-orange-500 text-stone-400 hover:text-orange-400 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-upload-2-line" />
              Replace Image
            </button>
            <input ref={logoReplaceRef} type="file" accept="image/*" className="hidden" onChange={handleLogoReplace} />
          </div>
        )}

        {/* Font (text/cta) */}
        {(isText || isCta) && (
          <div>
            <SectionLabel>Font</SectionLabel>
            <select
              value={selectedOverlay.fontFamily}
              onChange={(e) => onUpdateOverlay({ fontFamily: e.target.value })}
              className="w-full rounded-lg border border-stone-700 bg-stone-800 px-3 py-2 text-sm text-stone-200 focus:outline-none focus:border-orange-500 transition-colors cursor-pointer"
            >
              {FONT_OPTIONS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Font Size */}
        {!isLogo && (
          <div>
            <SectionLabel>Font Size — {selectedOverlay.fontSize}px</SectionLabel>
            <input
              type="range"
              min={10}
              max={80}
              value={selectedOverlay.fontSize}
              onChange={(e) => onUpdateOverlay({ fontSize: Number(e.target.value) })}
              className="w-full accent-orange-500 cursor-pointer"
            />
            <div className="flex justify-between text-xs text-stone-600 mt-1">
              <span>10px</span>
              <span>80px</span>
            </div>
          </div>
        )}

        {/* Font Weight (text/cta) */}
        {(isText || isCta) && (
          <div>
            <SectionLabel>Font Weight</SectionLabel>
            <div className="grid grid-cols-3 gap-1.5">
              {[400, 600, 700, 800, 900].map((w) => (
                <button
                  key={w}
                  onClick={() => onUpdateOverlay({ fontWeight: w })}
                  className={`py-1.5 rounded-lg text-xs transition-all cursor-pointer whitespace-nowrap ${
                    selectedOverlay.fontWeight === w
                      ? 'bg-orange-500 text-white font-bold'
                      : 'bg-stone-800 text-stone-400 hover:bg-stone-700'
                  }`}
                  style={{ fontWeight: w }}
                >
                  {w}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Text Color */}
        {!isLogo && (
          <div>
            <SectionLabel>Text Color</SectionLabel>
            <ColorSwatch
              value={selectedOverlay.color}
              onChange={(v) => onUpdateOverlay({ color: v })}
              label="Text color"
            />
          </div>
        )}

        {/* Background Color (cta/text) */}
        {(isCta || isText) && (
          <div>
            <SectionLabel>Background Color</SectionLabel>
            <div className="space-y-2">
              <ColorSwatch
                value={selectedOverlay.backgroundColor === 'transparent' ? '#000000' : selectedOverlay.backgroundColor}
                onChange={(v) => onUpdateOverlay({ backgroundColor: v })}
                label="Background color"
              />
              <button
                onClick={() => onUpdateOverlay({ backgroundColor: 'transparent' })}
                className={`text-xs px-2 py-1 rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                  selectedOverlay.backgroundColor === 'transparent'
                    ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                    : 'bg-stone-800 text-stone-500 hover:text-stone-300'
                }`}
              >
                Transparent
              </button>
            </div>
          </div>
        )}

        {/* Border Radius (cta) */}
        {isCta && (
          <div>
            <SectionLabel>Border Radius — {selectedOverlay.borderRadius}px</SectionLabel>
            <input
              type="range"
              min={0}
              max={50}
              value={selectedOverlay.borderRadius}
              onChange={(e) => onUpdateOverlay({ borderRadius: Number(e.target.value) })}
              className="w-full accent-orange-500 cursor-pointer"
            />
          </div>
        )}

        {/* CTA Icon */}
        {isCta && (
          <div>
            <SectionLabel>Icon</SectionLabel>
            <div className="grid grid-cols-4 gap-1.5">
              {[
                { icon: 'none', label: 'None' },
                { icon: 'ri-arrow-right-line', label: '→' },
                { icon: 'ri-phone-line', label: 'Phone' },
                { icon: 'ri-calendar-line', label: 'Cal' },
                { icon: 'ri-shopping-cart-line', label: 'Cart' },
                { icon: 'ri-map-pin-line', label: 'Pin' },
                { icon: 'ri-star-line', label: 'Star' },
                { icon: 'ri-flashlight-line', label: 'Flash' },
              ].map(({ icon, label }) => (
                <button
                  key={icon}
                  onClick={() => onUpdateOverlay({ icon: icon === 'none' ? undefined : icon })}
                  className={`flex flex-col items-center gap-1 py-2 rounded-lg text-xs transition-all cursor-pointer ${
                    (selectedOverlay.icon ?? 'none') === icon
                      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                      : 'bg-stone-800 text-stone-500 hover:bg-stone-700 hover:text-stone-300'
                  }`}
                  title={label}
                >
                  {icon === 'none' ? (
                    <i className="ri-forbid-line text-sm" />
                  ) : (
                    <i className={`${icon} text-sm`} />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Opacity */}
        <div>
          <SectionLabel>Opacity — {Math.round(selectedOverlay.opacity * 100)}%</SectionLabel>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={selectedOverlay.opacity}
            onChange={(e) => onUpdateOverlay({ opacity: Number(e.target.value) })}
            className="w-full accent-orange-500 cursor-pointer"
          />
        </div>

        {/* Width */}
        <div>
          <SectionLabel>Width — {Math.round(selectedOverlay.width)}%</SectionLabel>
          <input
            type="range"
            min={5}
            max={100}
            value={selectedOverlay.width}
            onChange={(e) => onUpdateOverlay({ width: Number(e.target.value) })}
            className="w-full accent-orange-500 cursor-pointer"
          />
        </div>

        {/* Position */}
        <div>
          <SectionLabel>Position</SectionLabel>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-stone-500 mb-1 block">X — {Math.round(selectedOverlay.x)}%</label>
              <input
                type="range"
                min={0}
                max={95}
                value={selectedOverlay.x}
                onChange={(e) => onUpdateOverlay({ x: Number(e.target.value) })}
                className="w-full accent-orange-500 cursor-pointer"
              />
            </div>
            <div>
              <label className="text-xs text-stone-500 mb-1 block">Y — {Math.round(selectedOverlay.y)}%</label>
              <input
                type="range"
                min={0}
                max={95}
                value={selectedOverlay.y}
                onChange={(e) => onUpdateOverlay({ y: Number(e.target.value) })}
                className="w-full accent-orange-500 cursor-pointer"
              />
            </div>
          </div>
        </div>

        {/* Animation */}
        <div>
          <SectionLabel>Animation</SectionLabel>
          <div className="space-y-1">
            {ANIMATION_OPTIONS.map((anim) => (
              <button
                key={anim.value}
                onClick={() => onUpdateOverlay({ animation: anim.value as VideoAnimation })}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  selectedOverlay.animation === anim.value
                    ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                    : 'bg-stone-800 text-stone-400 hover:bg-stone-700 hover:text-stone-200'
                }`}
              >
                <i className={`${anim.icon} text-sm shrink-0`} />
                {anim.label}
              </button>
            ))}
          </div>
        </div>

        {/* Timing */}
        <div>
          <SectionLabel>Timing</SectionLabel>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-stone-500 mb-1 block">
                Start Time — {selectedOverlay.startTime.toFixed(1)}s
              </label>
              <input
                type="range"
                min={0}
                max={8}
                step={0.1}
                value={selectedOverlay.startTime}
                onChange={(e) => onUpdateOverlay({ startTime: Number(e.target.value) })}
                className="w-full accent-orange-500 cursor-pointer"
              />
            </div>
            <div>
              <label className="text-xs text-stone-500 mb-1 block">
                Duration — {selectedOverlay.duration.toFixed(1)}s
              </label>
              <input
                type="range"
                min={0.5}
                max={10}
                step={0.1}
                value={selectedOverlay.duration}
                onChange={(e) => onUpdateOverlay({ duration: Number(e.target.value) })}
                className="w-full accent-orange-500 cursor-pointer"
              />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
